import React from "react";
import { useDropzone } from "react-dropzone";
import classnames from "classnames";
import PropTypes from "prop-types";

import FormErrors from "../FormErrors";

import DropzoneFile from "./DropzoneFile";
import fileTypeDetails from "./helpers/fileTypeDetails";
import rejectionReasons from "./helpers/rejectionReasons";

const addStatus = (file, status) => {
  file.accepted = status;
  return file;
};

const Dropzone = ({
  className,
  fileType,
  onChange,
  value: files,
  ...props
}) => {
  const acceptedMimeTypes = fileTypeDetails(fileType).mimeTypes;

  const removeFile = toRemove => onChange(files.filter(file => file !== toRemove));

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted, rejected) => {
      const acceptedWithStatus = accepted.map(file => addStatus(file, true));
      const rejectedWithStatus = rejected.map(file => addStatus(file, false));
      onChange([...acceptedWithStatus, ...rejectedWithStatus]);
    },
    accept: acceptedMimeTypes,
    ...props
  });

  return (
    <div
      className={classnames(
        "dropzone dz-clickable",
        className,
        { "dz-drag-hover": isDragActive },
        { "dz-started dz-max-files-reached": files && files.length > 0 }
      )}
    >
      <div {...getRootProps({ className: "dz-default dz-message" })}>
        <input {...getInputProps({ name: "dropzone" })} />
        <p>{fileTypeDetails(fileType).message}</p>
      </div>
      {files && files.map(file => (
        <div key={file.lastModified} className="dropzone-file-row">
          <DropzoneFile
            file={file}
            onCancel={removeFile}
          />
          {!file.accepted && (
            <FormErrors
              errors={rejectionReasons(file, files, acceptedMimeTypes, props)}
              className="d-block"
            />
          )}
        </div>
      ))}
    </div>
  );
};

Dropzone.defaultProps = {
  disabled: false,
  fileType: 'any',
  multiple: false
};

Dropzone.propTypes = {
  /**
   * class names to be added to the outer dropzone component
   */
  className: PropTypes.string,
  /**
   * Indicate whether the dropzone cannot receive any input
   */
  disabled: PropTypes.bool,
  /**
   * Comma-separated list of accepted mime types.
   * If given one of the following values, it automatically generates the list of mime types for you:
   * "image", "excel", "spreadsheet", "any"
   */
  fileType: PropTypes.string,
  /**
   * Maximum file size (in bytes) - used to accept or reject files
   */
  maxSize: PropTypes.number,
  /**
   * Minimum file size (in bytes) - used to accept or reject files
   */
  minSize: PropTypes.number,
  /**
   * Allow drag 'n' drop (or selection from the file dialog) of multiple files
   */
  multiple: PropTypes.bool,
  /**
   * Callback for when files in dropzone change.
   * The callback receives a list of file objects - https://developer.mozilla.org/en-US/docs/Web/API/File.
   * Additionally, each file will contain an 'accepted' boolean attribute set based on the Dropzone requirements provided.
   */
  onChange: PropTypes.func,
  /**
   * Files to be displayed in the dropzone
   */
  value: PropTypes.array.isRequired
};

export default Dropzone;
